import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading';
import { clearOrder, getAdminOrderById, updateOrderStatus } from '../../features/order/orderSlice';
import { formatPrice as formatCurrency } from '../../utils/currency';

function AdminOrderInspect() {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.auth);
    const { order, isLoading, isError, message } = useSelector((state) => state.order);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }

        if (orderId) {
            dispatch(getAdminOrderById(orderId));
        }

        return () => dispatch(clearOrder());
    }, [dispatch, orderId, user, navigate]);

    useEffect(() => {
        if (isError && message) {
            toast.error(message);
        }
    }, [isError, message]);

    const formatPrice = (price) =>
        formatCurrency(price, { currency: order?.payment?.currency || order?.currency });

    const formatDate = (dateString) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const getStatusBadge = (status) => {
        const statusTokens = {
            unpaid: { bg: 'rgba(233, 130, 81, 0.18)', color: 'var(--color-accent)' },
            processing: { bg: 'rgba(0, 86, 112, 0.15)', color: 'var(--color-deep-teal)' },
            shipped: { bg: 'rgba(44, 107, 80, 0.15)', color: 'var(--color-forest)' },
            delivered: { bg: 'var(--color-deep-teal)', color: 'var(--color-surface)' },
            cancelled: { bg: 'var(--color-feedback-warn-soft)', color: 'var(--color-accent)' },
        };

        const { bg, color } = statusTokens[status] || { bg: 'var(--color-muted)', color: 'var(--color-surface)' };

        return (
            <span
                style={{
                    backgroundColor: bg,
                    color,
                    padding: '6px 12px',
                    borderRadius: '999px',
                    fontSize: '0.85em',
                    fontWeight: 'bold',
                    letterSpacing: '0.04em',
                }}
            >
                {status?.toUpperCase()}
            </span>
        );
    };

    const handleStatusChange = (newStatus) => {
        if (!order?._id) return;
        dispatch(updateOrderStatus({ orderId: order._id, status: newStatus }))
            .unwrap()
            .then(() => toast.success('Order status updated'))
            .catch(() => {});
    };

    const totals = useMemo(() => {
        if (!order) return null;
        return [
            { label: 'Items', value: formatPrice(order.itemPrice) },
            { label: 'Shipping', value: formatPrice(order.shippingPrice) },
            { label: 'Tax', value: formatPrice(order.taxPrice) },
            order.shipping_discount ? { label: 'Shipping Discount', value: `- ${formatPrice(order.shipping_discount)}` } : null,
            order.product_discount ? { label: 'Product Discount', value: `- ${formatPrice(order.product_discount)}` } : null,
            { label: 'Total', value: formatPrice(order.totalPrice), emphasize: true },
        ].filter(Boolean);
    }, [order]);

    if (isLoading || !order) {
        return <Loading />;
    }

    const client = order.client || {};

    return (
        <div className="admin-container">
            <div className="admin-header-container">
                <div>
                    <p style={{ margin: 0, color: 'var(--color-ink-soft)' }}>Order #{order.order_num}</p>
                    <h1 className="admin-header" style={{ marginBottom: 6 }}>Inspect Order</h1>
                    <p style={{ margin: 0, color: 'var(--color-ink-soft)' }}>Placed on {formatDate(order.createdAt)}</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                    {getStatusBadge(order.status)}
                    <button className="btn btn-secondary btn-close" onClick={() => navigate(-1)} title="Back to orders">
                        ←
                    </button>
                </div>
            </div>

            <section className="form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h2 className="admin-subheader" style={{ margin: 0 }}>Fulfilment</h2>
                    <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="form-select"
                        style={{ maxWidth: 220 }}
                    >
                        <option value="unpaid">Unpaid</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Shipping Method</span>
                        <p style={{ margin: '4px 0 0' }}>{order.shipping_method || 'Not specified'}</p>
                    </div>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Payment Status</span>
                        <p style={{ margin: '4px 0 0', textTransform: 'capitalize' }}>{order.payment?.status || 'N/A'}</p>
                    </div>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Payment Method</span>
                        <p style={{ margin: '4px 0 0' }}>{order.payment?.method || 'N/A'}</p>
                    </div>
                </div>
            </section>

            <section className="form-section">
                <h2 className="admin-subheader">Customer</h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20 }}>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Name</span>
                        <p style={{ margin: '4px 0 0', fontWeight: 600 }}>
                            {client.firstName} {client.lastName}
                        </p>
                    </div>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Email</span>
                        <p style={{ margin: '4px 0 0' }}>{client.email}</p>
                    </div>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Phone</span>
                        <p style={{ margin: '4px 0 0' }}>{client.phone || 'Not provided'}</p>
                    </div>
                    <div>
                        <span style={{ color: 'var(--color-ink-soft)', fontSize: '0.85em' }}>Address</span>
                        <p style={{ margin: '4px 0 0' }}>
                            {[client.address, client.city, client.postcode].filter(Boolean).join(', ') || 'Not provided'}
                        </p>
                    </div>
                </div>
            </section>

            <section className="form-section">
                <h2 className="admin-subheader">Items</h2>
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Product</th>
                            <th>Variant</th>
                            <th>Qty</th>
                            <th>Price</th>
                            <th>Subtotal</th>
                        </tr>
                    </thead>
                    <tbody>
                        {order.orderItems.map((item, idx) => (
                            <tr key={`${item.product}-${idx}`}>
                                <td>
                                    <strong>{item.name}</strong>
                                    <br />
                                    <small style={{ color: 'var(--color-ink-soft)' }}>{item.category}</small>
                                </td>
                                <td>{item.variant_name || '—'}</td>
                                <td>{item.quantity}</td>
                                <td>{formatPrice(item.price)}</td>
                                <td>{formatPrice(item.price * item.quantity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>

            <section className="form-section" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <h2 className="admin-subheader" style={{ marginBottom: 4 }}>Totals</h2>
                {totals?.map((row) => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', fontWeight: row.emphasize ? 700 : 500 }}>
                        <span>{row.label}</span>
                        <span>{row.value}</span>
                    </div>
                ))}
                {order.coupon && (
                    <p style={{ marginTop: 8, color: 'var(--color-ink-soft)', fontSize: '0.9em' }}>
                        Coupon applied: <strong>{order.coupon.code}</strong>
                    </p>
                )}
            </section>
        </div>
    );
}

export default AdminOrderInspect;
